import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import GoogleDrivePicker from './GoogleDrivePicker';
import { useOrgBrand } from '../context/OrgBrandContext';
import { useModal } from '../context/ModalContext';
import '../styles/SourcesPanel.css';
import CustomSelect from './CustomSelect';
import { uploadToS3, validateFile } from '../utils/s3Upload';

const SourcesPanel = ({ user }) => {
  const modal = useModal();
  const {
    selectedBrand: contextBrand,
    selectedOrganization: contextOrg,
    organizations,
    brands,
    loading: contextLoading
  } = useOrgBrand();
  const [sources, setSources] = useState([]);

  // Organization and brand selection
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // View mode: 'view', 'add', 'create-ideas', or 'create-content'
  const [viewMode, setViewMode] = useState('view');
  const [addSourceTab, setAddSourceTab] = useState('url'); // 'url', 'text', 'upload', or 'drive'

  // Create Ideas/Content mode state
  const [selectedSourcesForGeneration, setSelectedSourcesForGeneration] = useState([]);
  const [selectedSourcesDetails, setSelectedSourcesDetails] = useState([]); // Store full source objects
  const [selectedIdeasForGeneration, setSelectedIdeasForGeneration] = useState([]); // NEW: Selected idea IDs
  const [selectedIdeasDetails, setSelectedIdeasDetails] = useState([]); // NEW: Store full idea objects
  const [availableIdeas, setAvailableIdeas] = useState([]); // NEW: Ideas for the brand
  const [sourceOrIdeaTab, setSourceOrIdeaTab] = useState('sources'); // NEW: 'sources' or 'ideas'
  const [generationOrgId, setGenerationOrgId] = useState(null);
  const [generationBrandId, setGenerationBrandId] = useState(null);
  const [generationAmount, setGenerationAmount] = useState(5); // Amount of ideas to generate (5-20)
  const [contentQuantity, setContentQuantity] = useState(1); // NEW: Number of content pieces (1-5)
  const [contentType, setContentType] = useState('social');
  const [contentLength, setContentLength] = useState('medium');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [generatingIdeas, setGeneratingIdeas] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false)
  const [urlInput, setUrlInput] = useState('');
  const [urlName, setUrlName] = useState('');
  const [textInput, setTextInput] = useState('');
  const [textName, setTextName] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('');
  const [audioVideoFile, setAudioVideoFile] = useState(null);
  // Shared across the merged "Upload File" tab - image/pdf/audio/video all
  // share one name field and one dropzone; uploadKind records which of the
  // three the selected file was auto-detected as, so the right handler and
  // "Add..." button label are shown once a file lands.
  const [uploadName, setUploadName] = useState('');
  const [uploadKind, setUploadKind] = useState(null); // null | 'image' | 'pdf' | 'audiovideo'
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileInfo, setUploadFileInfo] = useState(null);
  const [isDraggingUpload, setIsDraggingUpload] = useState(false);
  const [driveFile, setDriveFile] = useState(null);
  const [driveName, setDriveName] = useState('');
  const [addingUrl, setAddingUrl] = useState(false);
  const [addingText, setAddingText] = useState(false);
  const [addingMedia, setAddingMedia] = useState(false);
  const [addingPdf, setAddingPdf] = useState(false);
  const [addingAudioVideo, setAddingAudioVideo] = useState(false);
  const [addingDrive, setAddingDrive] = useState(false);

  // View source modal state
  const [viewSourceModal, setViewSourceModal] = useState({ open: false, source: null });
  const [sourceCopied, setSourceCopied] = useState(false);

  // Retranscription state (for upload flow)
  const [retranscribing, setRetranscribing] = useState(false);
  const [showTranscriptionPreview, setShowTranscriptionPreview] = useState(false);
  const [transcriptionResult, setTranscriptionResult] = useState('');
  const [alternativeTranscription, setAlternativeTranscription] = useState('');
  const [showTranscriptionComparison, setShowTranscriptionComparison] = useState(false);

  // Auto-select org from context when data loads - only if no org is currently selected
  useEffect(() => {
    if (organizations.length > 0 && contextOrg && !selectedOrg) {
      const matchingOrg = organizations.find(o =>
        (o._id === contextOrg._id) || (o.id === contextOrg.id)
      );
      if (matchingOrg) {
        setSelectedOrg(matchingOrg);
      }
    }
  }, [organizations, contextOrg, selectedOrg]);

  // Auto-select brand from context when org is selected - only if no brand is currently selected
  useEffect(() => {
    if (selectedOrg && brands.length > 0 && contextBrand && !selectedBrand) {
      const matchingBrand = brands.find(b =>
        (b._id === contextBrand._id || b.id === contextBrand.id) &&
        (b.organization_id === selectedOrg._id || b.organization_id === selectedOrg.id)
      );
      if (matchingBrand) {
        setSelectedBrand(matchingBrand);
      }
    }
  }, [selectedOrg, brands, contextBrand, selectedBrand]);

  // Extract the actual ID we use for filtering
  const selectedBrandId = selectedBrand ? (selectedBrand._id || selectedBrand.id) : null;

  // Fetch sources when brand changes in view mode
  useEffect(() => {
    if (selectedBrand && (viewMode === 'view' || viewMode === 'add')) {
      fetchSources();
    } else if (viewMode === 'view' || viewMode === 'add') {
      setSources([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandId, viewMode]);

  // Fetch sources when generation brand changes in create modes
  useEffect(() => {
    const fetchSourcesForGeneration = async () => {
      if ((viewMode === 'create-ideas' || viewMode === 'create-content') && generationBrandId) {
        try {
          const response = await axios.get('/api/sources', {
            params: { brand_id: generationBrandId },
            withCredentials: true
          });

          if (response.data.success) {
            setSources(response.data.sources);
          }
        } catch (error) {
          console.error('Error fetching sources for generation:', error);
          modal.error('Failed to load sources');
        }
      }
    };

    fetchSourcesForGeneration();
  }, [generationBrandId, viewMode]);

  // NEW: Fetch ideas when generation brand changes in create-content mode
  useEffect(() => {
    const fetchIdeasForGeneration = async () => {
      if (viewMode === 'create-content' && generationBrandId && generationOrgId) {
        try {
          const response = await axios.get('/api/ideas/for-brand', {
            params: {
              brand_id: generationBrandId,
              organization_id: generationOrgId
            },
            withCredentials: true
          });

          if (response.data.success) {
            setAvailableIdeas(response.data.ideas);
          }
        } catch (error) {
          console.error('Error fetching ideas for generation:', error);
          // Don't set error, just log it (ideas are optional)
        }
      } else {
        setAvailableIdeas([]);
      }
    };

    fetchIdeasForGeneration();
  }, [generationBrandId, generationOrgId, viewMode]);

  const fetchSources = async () => {
    try {
      const params = {};
      if (selectedBrand) params.brand_id = selectedBrand._id || selectedBrand.id;

      const response = await axios.get('/api/sources', {
        params,
        withCredentials: true
      });

      if (response.data.success) {
        setSources(response.data.sources);
      }
    } catch (error) {
      console.error('Error fetching sources:', error);
      modal.error('Failed to load sources');
    }
  };

  const handleAddUrl = async () => {
    if (!urlInput.trim()) {
      modal.error('Please enter a URL');
      return;
    }

    if (!urlName.trim()) {
      modal.error('Please enter a name for this source');
      return;
    }

    try {
      new URL(urlInput);
    } catch (e) {
      modal.error('Please enter a valid URL');
      return;
    }

    if (!selectedBrand || !selectedOrg) {
      modal.error('Please select a brand and organization');
      return;
    }

    try {
      setAddingUrl(true);

      const response = await axios.post('/api/sources', {
        source_type: 'url',
        brand_id: selectedBrand._id || selectedBrand.id,
        organization_id: selectedOrg._id || selectedOrg.id,
        name: urlName,
        content: urlInput
      }, { withCredentials: true });

      if (response.data.success) {
        modal.success('URL source added successfully!');
        setUrlInput('');
        setUrlName('');
        fetchSources();
        setViewMode('view');
      }
    } catch (error) {
      console.error('Error adding URL source:', error);
      modal.error(error.response?.data?.error || 'Failed to add URL source');
    } finally {
      setAddingUrl(false);
    }
  };

  const handleAddText = async () => {
    if (!textInput.trim()) {
      modal.error('Please enter some text');
      return;
    }

    if (!textName.trim()) {
      modal.error('Please enter a name for this source');
      return;
    }

    if (!selectedBrand || !selectedOrg) {
      modal.error('Please select a brand and organization');
      return;
    }

    try {
      setAddingText(true);

      const response = await axios.post('/api/sources', {
        source_type: 'text',
        brand_id: selectedBrand._id || selectedBrand.id,
        organization_id: selectedOrg._id || selectedOrg.id,
        name: textName,
        content: textInput
      }, { withCredentials: true });

      if (response.data.success) {
        modal.success('Text source added successfully!');
        setTextInput('');
        setTextName('');
        fetchSources();
        setViewMode('view');
      }
    } catch (error) {
      console.error('Error adding text source:', error);
      modal.error(error.response?.data?.error || 'Failed to add text source');
    } finally {
      setAddingText(false);
    }
  };

  const handleMediaFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (images only)
    if (!file.type.startsWith('image/')) {
      modal.error('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      modal.error('File size must be less than 10MB');
      return;
    }

    setMediaFile(file);

    // Generate preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setMediaPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Resets everything owned by the merged "Upload File" tab (image/pdf/audio/video
  // all share this one tab) - called after a successful add and when the user
  // removes the selected file to pick a different one.
  const resetUploadTab = () => {
    setUploadKind(null);
    setUploadName('');
    setMediaFile(null);
    setPdfFile(null);
    setPdfFileName('');
    setAudioVideoFile(null);
    setUploadFileInfo(null);
    setUploadProgress(0);
    setUploadingFile(false);
  };

  const ALLOWED_UPLOAD_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
  const UPLOAD_FILE_ACCEPT = [...ALLOWED_UPLOAD_IMAGE_TYPES, 'application/pdf'].join(',') + ',audio/*,video/*';

  // Single entry point for the merged dropzone - detects whether the dropped/
  // selected file is an image, PDF, or audio/video, validates it against that
  // type's own size limit, and routes it to the matching upload path. Images and
  // PDFs upload to S3 immediately (same as the old separate tabs did); audio/video
  // is held locally until "Add & Transcribe" is clicked.
  const handleUploadFileSelect = async (file) => {
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';
    const isVideo = file.type.startsWith('video/');
    const isAudio = file.type.startsWith('audio/');

    if (!isImage && !isPdf && !isVideo && !isAudio) {
      modal.error('Unsupported file type. Please upload an image, PDF, audio, or video file.');
      return;
    }

    const metadata = {
      brand_id: selectedBrand?._id || selectedBrand?.id,
      organization_id: selectedOrg?._id || selectedOrg?.id
    };

    if (isImage) {
      const validation = validateFile(file, 5, ALLOWED_UPLOAD_IMAGE_TYPES);
      if (!validation.valid) {
        modal.error(validation.error);
        return;
      }

      setUploadKind('image');
      setUploadingFile(true);
      setUploadProgress(0);
      try {
        const result = await uploadToS3(file, 'source_image', metadata, setUploadProgress);
        if (result.success) {
          setMediaFile(result.file_url);
          setUploadFileInfo({ name: file.name, size: `${(file.size / 1024).toFixed(2)} KB` });
        } else {
          modal.error(result.error || 'Image upload failed. Please try again.');
          setUploadKind(null);
        }
      } catch (err) {
        console.error('Error uploading image:', err);
        modal.error('Image upload failed. Please try again.');
        setUploadKind(null);
      } finally {
        setUploadingFile(false);
      }
      return;
    }

    if (isPdf) {
      const validation = validateFile(file, 50, ['application/pdf']);
      if (!validation.valid) {
        modal.error(validation.error);
        return;
      }

      setUploadKind('pdf');
      setUploadingFile(true);
      setUploadProgress(0);
      try {
        const result = await uploadToS3(file, 'source_pdf', metadata, setUploadProgress);
        if (result.success) {
          setPdfFile(result.file_url);
          setPdfFileName(file.name);
          setUploadFileInfo({ name: file.name, size: `${(file.size / (1024 * 1024)).toFixed(2)} MB` });
        } else {
          modal.error(result.error || 'PDF upload failed. Please try again.');
          setUploadKind(null);
        }
      } catch (err) {
        console.error('Error uploading PDF:', err);
        modal.error('PDF upload failed. Please try again.');
        setUploadKind(null);
      } finally {
        setUploadingFile(false);
      }
      return;
    }

    // Audio/video - held locally, transcribed when "Add & Transcribe" is clicked.
    const sizeMB = file.size / (1024 * 1024);
    const maxSize = isVideo ? 200 : 50;
    if (sizeMB > maxSize) {
      modal.error(`File size exceeds ${maxSize}MB limit`);
      return;
    }

    setAudioVideoFile(file);
    setUploadKind('audiovideo');
  };

  const handleUploadDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingUpload(true);
  };

  const handleUploadDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingUpload(false);
  };

  const handleUploadDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleUploadDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingUpload(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleUploadFileSelect(files[0]);
    }
  };

  const handleAddMedia = async () => {
    if (!mediaFile) {
      modal.error('Please select an image file');
      return;
    }

    if (!uploadName.trim()) {
      modal.error('Please enter a name for this source');
      return;
    }

    if (!selectedBrand || !selectedOrg) {
      modal.error('Please select a brand and organization');
      return;
    }

    try {
      setAddingMedia(true);

      // Image file is already uploaded to S3, mediaFile contains the S3 URL
      const response = await axios.post('/api/sources', {
        source_type: 'image',
        brand_id: selectedBrand._id || selectedBrand.id,
        organization_id: selectedOrg._id || selectedOrg.id,
        name: uploadName,
        s3_url: mediaFile
      }, { withCredentials: true });

      if (response.data.success) {
        modal.success('Image source added successfully!');
        resetUploadTab();
        fetchSources();
        setViewMode('view');
      }
    } catch (error) {
      console.error('Error adding media source:', error);
      modal.error(error.response?.data?.error || 'Failed to add media source');
    } finally {
      setAddingMedia(false);
    }
  };

  const handleAddPdf = async () => {
    if (!pdfFile) {
      modal.error('Please select a PDF file');
      return;
    }

    if (!uploadName.trim()) {
      modal.error('Please enter a name for this source');
      return;
    }

    if (!selectedBrand || !selectedOrg) {
      modal.error('Please select a brand and organization');
      return;
    }

    try {
      setAddingPdf(true);

      // PDF file is already uploaded to S3, pdfFile contains the S3 URL
      const response = await axios.post('/api/sources', {
        source_type: 'pdf',
        brand_id: selectedBrand._id || selectedBrand.id,
        organization_id: selectedOrg._id || selectedOrg.id,
        name: uploadName,
        s3_url: pdfFile,
        file_name: pdfFileName
      }, { withCredentials: true });

      if (response.data.success) {
        modal.success('PDF source added successfully!');
        resetUploadTab();
        fetchSources();
        setViewMode('view');
      }
    } catch (error) {
      console.error('Error adding PDF source:', error);
      modal.error(error.response?.data?.error || 'Failed to add PDF source');
    } finally {
      setAddingPdf(false);
    }
  };

  const handleAddAudioVideo = async () => {
    if (!audioVideoFile) {
      modal.error('Please select an audio or video file');
      return;
    }

    if (!uploadName.trim()) {
      modal.error('Please enter a name for this source');
      return;
    }

    if (!selectedBrand || !selectedOrg) {
      modal.error('Please select a brand and organization');
      return;
    }

    try {
      setAddingAudioVideo(true);

      const isVideo = audioVideoFile.type.startsWith('video/');
      modal.success(isVideo ? 'Transcribing video...' : 'Transcribing audio...');

      // Send file via FormData to transcription endpoint
      const formData = new FormData();
      formData.append('file', audioVideoFile);
      formData.append('use_alternative', 'false');

      const transcribeResponse = await axios.post('/api/audio-video/transcribe', formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (transcribeResponse.data.success) {
        setTranscriptionResult(transcribeResponse.data.text);
        setShowTranscriptionPreview(true);
        setAddingAudioVideo(false);
      }
    } catch (error) {
      console.error('Error transcribing audio/video:', error);
      modal.error(error.response?.data?.error || 'Failed to transcribe audio/video');
      setAddingAudioVideo(false);
    }
  };

  const handleRetranscribeAudio = async () => {
    setRetranscribing(true);

    try {
      if (!audioVideoFile) {
        modal.error('File not available for retranscription.\nPlease keep file selected.');
        setRetranscribing(false);
        return;
      }

      // Send file again with alternative transcription method
      const formData = new FormData();
      formData.append('file', audioVideoFile);
      formData.append('use_alternative', 'true');

      const response = await axios.post('/api/audio-video/transcribe', formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setAlternativeTranscription(response.data.text);
        setShowTranscriptionComparison(true);
      } else {
        modal.error(response.data.error || 'Failed to retranscribe');
      }
    } catch (error) {
      console.error('Error retranscribing:', error);
      modal.error(error.response?.data?.error || 'Failed to retranscribe');
    } finally {
      setRetranscribing(false);
    }
  };

  const handleSaveTranscription = async (selectedTranscription) => {
    const confirmed = await modal.confirm(
      'Are you sure you want to save this transcription?',
      'Confirm Save'
    );

    if (!confirmed) return;

    try {
      setAddingAudioVideo(true);

      const isVideo = audioVideoFile && audioVideoFile.type.startsWith('video/');

      // Now save the source with the chosen transcription
      const response = await axios.post('/api/sources', {
        source_type: 'transcription',
        brand_id: selectedBrand._id || selectedBrand.id,
        organization_id: selectedOrg._id || selectedOrg.id,
        name: uploadName,
        content: selectedTranscription,  // Save transcription as content
        file_name: audioVideoFile ? audioVideoFile.name : 'audio.mp3',
        metadata: {
          original_file_type: isVideo ? 'video' : 'audio',
          transcribed_in_memory: true
        }
      }, { withCredentials: true });

      if (response.data.success) {
        modal.success('Source saved successfully!');
        // Reset everything
        resetUploadTab();
        setShowTranscriptionPreview(false);
        setShowTranscriptionComparison(false);
        setTranscriptionResult('');
        setAlternativeTranscription('');
        fetchSources();
        setViewMode('view');
      }
    } catch (error) {
      console.error('Error saving source:', error);
      modal.error(error.response?.data?.error || 'Failed to save source');
    } finally {
      setAddingAudioVideo(false);
    }
  };

  const handleAddDrive = async () => {
    if (!driveFile) {
      modal.error('Please select a file from Google Drive');
      return;
    }

    if (!driveName.trim()) {
      modal.error('Please enter a name for this source');
      return;
    }

    if (!selectedBrand || !selectedOrg) {
      modal.error('Please select a brand and organization');
      return;
    }

    try {
      setAddingDrive(true);

      const response = await axios.post('/api/sources/google-drive/import', {
        file_id: driveFile.id,
        file_name: driveName,
        mime_type: driveFile.mimeType,
        brand_id: selectedBrand._id || selectedBrand.id,
        organization_id: selectedOrg._id || selectedOrg.id
      }, { withCredentials: true });

      if (response.data.success) {
        modal.success('Google Drive document imported successfully!');
        setDriveFile(null);
        setDriveName('');
        fetchSources();
        setViewMode('view');
      }
    } catch (error) {
      console.error('Error importing Google Drive document:', error);
      modal.error(error.response?.data?.error || 'Failed to import Google Drive document');
    } finally {
      setAddingDrive(false);
    }
  };

  const handleDeleteSource = async (sourceId) => {
    const result = await modal.confirm('Are you sure you want to delete this source?', 'Delete Source');
    if (!result) {
      return;
    }

    try {
      const response = await axios.delete(`/api/sources/${sourceId}`, {
        withCredentials: true
      });

      if (response.data.success) {
        modal.success('Source deleted successfully!');
        fetchSources();
      }
    } catch (error) {
      console.error('Error deleting source:', error);
      modal.error(error.response?.data?.error || 'Failed to delete source');
    }
  };

  // Quick action handlers - switches modes and pre-selects source
  const handleQuickCreateIdeas = (source) => {
    setViewMode('create-ideas');
    setGenerationOrgId(selectedOrg?._id || selectedOrg?.id);
    setGenerationBrandId(selectedBrand?._id || selectedBrand?.id);
    setSelectedSourcesForGeneration([source._id]);
    setSelectedSourcesDetails([source]);
  };

  const handleQuickCreateContent = (source) => {
    setViewMode('create-content');
    setGenerationOrgId(selectedOrg?._id || selectedOrg?.id);
    setGenerationBrandId(selectedBrand?._id || selectedBrand?.id);
    setSelectedSourcesForGeneration([source._id]);
    setSelectedSourcesDetails([source]);
    setSelectedIdeasForGeneration([]);
    setSelectedIdeasDetails([]);
    setContentType('social');
    setContentQuantity(1);
    setSourceOrIdeaTab('sources');
  };

  // Toggle source selection in create modes
  const toggleSourceSelection = (source) => {
    const sourceId = source._id;
    setSelectedSourcesForGeneration(prev => {
      if (prev.includes(sourceId)) {
        return prev.filter(id => id !== sourceId);
      } else {
        return [...prev, sourceId];
      }
    });
    setSelectedSourcesDetails(prev => {
      const exists = prev.find(s => s._id === sourceId);
      if (exists) {
        return prev.filter(s => s._id !== sourceId);
      } else {
        return [...prev, source];
      }
    });
  };

  // Remove source from selected list
  const removeSelectedSource = (sourceId) => {
    setSelectedSourcesForGeneration(prev => prev.filter(id => id !== sourceId));
    setSelectedSourcesDetails(prev => prev.filter(s => s._id !== sourceId));
  };

  // NEW: Toggle idea selection for generation
  const toggleIdeaSelection = (idea) => {
    const ideaId = idea.id;
    setSelectedIdeasForGeneration(prev => {
      if (prev.includes(ideaId)) {
        return prev.filter(id => id !== ideaId);
      } else {
        return [...prev, ideaId];
      }
    });
    setSelectedIdeasDetails(prev => {
      const exists = prev.find(i => i.id === ideaId);
      if (exists) {
        return prev.filter(i => i.id !== ideaId);
      } else {
        return [...prev, idea];
      }
    });
  };

  // NEW: Remove idea from selected list
  const removeSelectedIdea = (ideaId) => {
    setSelectedIdeasForGeneration(prev => prev.filter(id => id !== ideaId));
    setSelectedIdeasDetails(prev => prev.filter(i => i.id !== ideaId));
  };

  // Helper functions to get org/brand names
  const getOrgName = (orgId) => {
    const org = organizations.find(o => (o._id === orgId || o.id === orgId));
    return org ? (org.organization_name || org.name) : 'Unknown Org';
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => (b._id === brandId || b.id === brandId));
    return brand ? (brand.brand_name || brand.name) : 'Unknown Brand';
  };

  // Generate ideas from selected sources
  const handleGenerateIdeas = async () => {
    if (selectedSourcesForGeneration.length === 0) {
      modal.error('Please select at least one source');
      return;
    }

    if (!generationBrandId || !generationOrgId) {
      modal.error('Please select an organization and brand');
      return;
    }

    setGeneratingIdeas(true);

    try {
      const response = await axios.post('/api/ideas/generate-from-sources', {
        source_ids: selectedSourcesForGeneration,
        brand_id: generationBrandId,
        organization_id: generationOrgId,
        amount: generationAmount
      }, { withCredentials: true });

      if (response.data.success) {
        if (response.data.status === 'partial_success') {
          await modal.alert(
            `${response.data.reason || `Generated ${response.data.ideas_count} quality ideas from source material.`} View them in the Ideas panel.`,
            'Ideas Generated'
          );
        } else {
          await modal.alert(
            `Generated ${response.data.ideas_count} ideas successfully! View them in the Ideas panel.`,
            'Ideas Generated'
          );
        }
        // Keep the view on 'create-ideas' instead of switching to 'view'
        setSelectedSourcesForGeneration([]);
        setSelectedSourcesDetails([]);
      } else {
        modal.error(
          response.data.reason ||
            'The source materials did not contain enough information to generate quality content ideas.'
        );
      }
    } catch (error) {
      console.error('Error generating ideas:', error);
      modal.error(error.response?.data?.error || 'Failed to generate ideas');
    } finally {
      setGeneratingIdeas(false);
    }
  };

  // Generate content from selected sources and/or ideas
  const handleGenerateContent = async () => {
    // NEW: Allow either sources OR ideas (or both)
    if (selectedSourcesForGeneration.length === 0 && selectedIdeasForGeneration.length === 0) {
      modal.error('Please select at least one source or idea');
      return;
    }

    if (!generationBrandId || !generationOrgId) {
      modal.error('Please select an organization and brand');
      return;
    }

    if (!contentType) {
      modal.error('Please select a content type');
      return;
    }

    setGeneratingContent(true);

    try {
      const response = await axios.post('/api/content-drafts/create-from-sources', {
        source_ids: selectedSourcesForGeneration,
        idea_ids: selectedIdeasForGeneration,
        quantity: contentQuantity,
        brand_id: generationBrandId,
        organization_id: generationOrgId,
        content_type: contentType,
        length: contentLength
      }, { withCredentials: true });

      if (response.data.success) {
        const draftsCount = response.data.drafts_created || 1;
        await modal.alert(
          `Created ${draftsCount} content draft${draftsCount > 1 ? 's' : ''} successfully! View ${draftsCount > 1 ? 'them' : 'it'} in the Content Drafts panel.`,
          'Content Created'
        );
        // Keep the view on 'create-content' instead of switching to 'view'
        setSelectedSourcesForGeneration([]);
        setSelectedSourcesDetails([]);
        setSelectedIdeasForGeneration([]);
        setSelectedIdeasDetails([]);
        setContentType('social');
        setContentLength('medium');
        setContentQuantity(1);
      }
    } catch (error) {
      console.error('Error generating content:', error);
      modal.error(error.response?.data?.error || 'Failed to generate content');
    } finally {
      setGeneratingContent(false);
    }
  };

  const getSourceIcon = (type) => {
    const iconMap = {
      'url': 'link',
      'text': 'doc',
      'image': 'picture',
      'video': 'play',
      'audio': 'play',
      'transcription': 'comment-empty',
      'squadcast_transcript': 'comment-empty',
      'meeting': 'comment-empty',
      'pdf': 'doc-text',
      'google_drive': 'google',
      'brand_profile': 'user'
    };
    return iconMap[type] || 'doc';
  };

  const getSourceTypeName = (source) => {
    // If passed a string (for backwards compatibility), treat as type
    const type = typeof source === 'string' ? source : source.source_type;

    // Check if it's a transcript with Squadcast metadata
    if ((type === 'transcription' || type === 'meeting') && typeof source === 'object') {
      const metadata = source.metadata || {};
      if (metadata.is_squadcast || metadata.squadcast_session_id) {
        return 'SQUADCAST TRANSCRIPT';
      }
    }

    // Map source types to display names
    const typeMap = {
      'meeting': 'TRANSCRIPT',
      'transcription': 'TRANSCRIPT',
      'squadcast_transcript': 'SQUADCAST TRANSCRIPT',
      'brand_profile': 'BRAND PROFILE',
      'url': 'URL',
      'text': 'TEXT',
      'image': 'IMAGE',
      'video': 'VIDEO',
      'audio': 'AUDIO',
      'pdf': 'PDF',
      'google_drive': 'GOOGLE DRIVE'
    };
    return typeMap[type] || type.toUpperCase();
  };

  const openSourceViewer = (source) => {
    setViewSourceModal({
      open: true,
      source: source
    });
  };

  const closeSourceViewer = () => {
    setViewSourceModal({ open: false, source: null });
    setSourceCopied(false);
  };

  const copySourceContent = async () => {
    const source = viewSourceModal.source;
    if (!source) return;

    let contentToCopy = '';

    if (source.source_type === 'url') {
      contentToCopy = source.original_url || source.content || '';
    } else if (source.source_type === 'meeting' || source.source_type === 'transcription') {
      // Handle transcript content which might be an object
      const content = source.content;
      if (typeof content === 'object' && content !== null) {
        contentToCopy = content.formatted || content.simple || JSON.stringify(content, null, 2);
      } else {
        contentToCopy = content || '';
      }
    } else {
      contentToCopy = source.content || '';
    }

    if (contentToCopy) {
      try {
        await navigator.clipboard.writeText(contentToCopy);
        setSourceCopied(true);
        setTimeout(() => setSourceCopied(false), 2000);
      } catch (error) {
        console.error('Error copying to clipboard:', error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };


  const selectOrg = (org) => {
    setSelectedOrg(org);
    setSelectedBrand(null); // Reset brand selection when changing org
    setViewMode('view'); // Reset to view mode
  };

  const selectBrand = (brand) => {
    setSelectedBrand(brand);
    setViewMode('view'); // Reset to view mode
  };

  // Get brands for selected organization
  const brandsForSelectedOrg = selectedOrg
    ? brands.filter(b => (b.organization_id === selectedOrg._id) || (b.organization_id === selectedOrg.id))
    : [];

  return (
    <div className="sources-panel-container">
      {contextLoading ? (
        <div className="sources-loading">
          <div className="sources-spinner"></div>
          <p>Loading organizations...</p>
        </div>
      ) : organizations.length === 0 ? (
        <div className="sources-empty-state">
          <Icon name="building" size="3rem" />
          <p>No organizations found</p>
          <span>You need to be assigned to an organization to manage sources</span>
        </div>
      ) : (
        <div className="sources-layout">
          {/* Left Sidebar - Organizations */}
          <div className="sources-sidebar">
            <h3>Organizations</h3>
            <div className="sources-orgs-list">
              {organizations
                .slice()
                .sort((a, b) => {
                  const nameA = (a.organization_name || a.name || '').toLowerCase();
                  const nameB = (b.organization_name || b.name || '').toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                .map(org => {
                const orgId = org._id || org.id;
                const isSelected = selectedOrg && ((selectedOrg._id === orgId) || (selectedOrg.id === orgId));
                const brandCount = brands.filter(b =>
                  (b.organization_id === orgId) || (b.organization_id === org._id)
                ).length;

                return (
                  <div
                    key={orgId}
                    className={`sources-org-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => selectOrg(org)}
                  >
                    <div className="sources-org-header">
                      {org.icon_url ? (
                        <BrandIcon src={org.icon_url} alt={org.organization_name || org.name} className="sources-org-logo" />
                      ) : (
                        <div className="sources-org-logo-placeholder">
                          <span>{(org.organization_name || org.name || 'O').charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                      <div className="sources-org-info">
                        <div className="sources-org-name">{org.organization_name || org.name}</div>
                        <div className="sources-org-count">
                          {brandCount} brand{brandCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel - Brands and Sources */}
          <div className="sources-detail-panel">
            {!selectedOrg ? (
              <div className="sources-no-selection">
                <Icon name="building" size="3rem" />
                <h3>Select an Organization</h3>
                <p>Choose an organization from the list to view its brands and sources</p>
              </div>
            ) : (
              <>
                {/* Organization Header */}
                <div className="sources-org-detail-header">
                  <div className="sources-org-title-row">
                    {selectedOrg.icon_url ? (
                      <BrandIcon src={selectedOrg.icon_url} alt={selectedOrg.organization_name || selectedOrg.name} className="sources-org-detail-logo" />
                    ) : (
                      <div className="sources-org-detail-logo-placeholder">
                        <span>{(selectedOrg.organization_name || selectedOrg.name || 'O').charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                    <h3>{selectedOrg.organization_name || selectedOrg.name}</h3>
                  </div>
                </div>

                {/* Brands Section */}
                <div className="sources-brands-section">
                  <h4>Brands ({brandsForSelectedOrg.length})</h4>
                  {brandsForSelectedOrg.length === 0 ? (
                    <p className="sources-no-brands">No brands in this organization</p>
                  ) : (
                    <div className="sources-brands-grid">
                      {brandsForSelectedOrg
                        .slice()
                        .sort((a, b) => {
                          const nameA = (a.brand_name || a.name || '').toLowerCase();
                          const nameB = (b.brand_name || b.name || '').toLowerCase();
                          return nameA.localeCompare(nameB);
                        })
                        .map(brand => {
                        const brandId = brand._id || brand.id;
                        const isSelected = selectedBrand && ((selectedBrand._id === brandId) || (selectedBrand.id === brandId));

                        return (
                          <div
                            key={brandId}
                            className={`sources-brand-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => selectBrand(brand)}
                          >
                            {brand.icon_url ? (
                              <BrandIcon src={brand.icon_url} alt={brand.brand_name || brand.name} className="sources-brand-logo" />
                            ) : (
                              <div className="sources-brand-logo-placeholder">
                                <span>{(brand.brand_name || brand.name || 'B').charAt(0).toUpperCase()}</span>
                              </div>
                            )}
                            <div className="sources-brand-name">{brand.brand_name || brand.name}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Sources Section */}
                {selectedBrand && (
                  <div className="sources-list-section">
                    <div className="sources-list-header">
                      <h4>
                        {viewMode === 'view'
                          ? `Sources for ${selectedBrand.brand_name || selectedBrand.name}`
                          : `Create Source for ${selectedBrand.brand_name || selectedBrand.name}`
                        }
                      </h4>
                    </div>

                    {/* Toggle Buttons - 4 modes */}
                    <div className="sources-mode-toggle">
                      <button
                        className={`sources-mode-btn ${viewMode === 'view' ? 'active' : ''}`}
                        onClick={() => setViewMode('view')}
                      >
                        <Icon name="eye" size="0.9rem" /> View Sources
                      </button>
                      <button
                        className={`sources-mode-btn ${viewMode === 'add' ? 'active' : ''}`}
                        onClick={() => setViewMode('add')}
                      >
                        <Icon name="plus" size="0.9rem" /> Add Source
                      </button>
                      <button
                        className={`sources-mode-btn ${viewMode === 'create-ideas' ? 'active' : ''}`}
                        onClick={() => {
                          setViewMode('create-ideas');
                          setGenerationOrgId(selectedOrg?._id || selectedOrg?.id);
                          setGenerationBrandId(selectedBrand?._id || selectedBrand?.id);
                          setSelectedSourcesForGeneration([]);
                          setSelectedSourcesDetails([]);
                        }}
                      >
                        <Icon name="lightbulb" size="0.9rem" /> Create Ideas
                      </button>
                      <button
                        className={`sources-mode-btn ${viewMode === 'create-content' ? 'active' : ''}`}
                        onClick={() => {
                          setViewMode('create-content');
                          setGenerationOrgId(selectedOrg?._id || selectedOrg?.id);
                          setGenerationBrandId(selectedBrand?._id || selectedBrand?.id);
                          setSelectedSourcesForGeneration([]);
                          setSelectedSourcesDetails([]);
                          setSelectedIdeasForGeneration([]);
                          setSelectedIdeasDetails([]);
                          setContentQuantity(1);
                          setSourceOrIdeaTab('sources');
                        }}
                      >
                        <Icon name="pencil" size="0.9rem" /> Create Content
                      </button>
                    </div>

                    {/* View Mode - Show Sources List */}
                    {viewMode === 'view' && (
                      <>
                        {sources.length === 0 ? (
                          <div className="sources-empty-small">
                            <Icon name="doc" size="2rem" />
                            <p>No sources yet for this brand</p>
                            <button
                              className="sources-create-first-btn"
                              onClick={() => setViewMode('add')}
                            >
                              Create Your First Source
                            </button>
                          </div>
                        ) : (
                      <div className="sources-table">
                        <div className="sources-table-header">
                          <div className="sources-col-type">Type</div>
                          <div className="sources-col-name">Name</div>
                          <div className="sources-col-date">Created</div>
                          <div className="sources-col-view">View</div>
                          <div className="sources-col-actions">Actions</div>
                        </div>
                        {sources.map((source) => (
                          <div key={source._id} className="sources-table-row">
                            <div className="sources-col-type">
                              <div className="sources-type-badge-row">
                                <span>{getSourceTypeName(source)}</span>
                              </div>
                            </div>
                            <div className="sources-col-name">
                              <div className="sources-name-cell">
                                <strong>{source.name}</strong>
                                {source.original_url && (
                                  <a
                                    href={source.original_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="sources-url-link"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Icon name="link" size="0.75rem" />
                                    {source.original_url.length > 40
                                      ? source.original_url.substring(0, 40) + '...'
                                      : source.original_url}
                                  </a>
                                )}
                                {source.content && source.source_type === 'text' && (
                                  <p className="sources-text-preview">
                                    {source.content.length > 100
                                      ? source.content.substring(0, 100) + '...'
                                      : source.content}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="sources-col-date">
                              {formatDate(source.created_at)}
                            </div>
                            <div className="sources-col-view">
                              <button
                                className="sources-view-icon-btn"
                                onClick={() => openSourceViewer(source)}
                                title="View source"
                              >
                                <Icon name="eye" size="1rem" />
                              </button>
                            </div>
                            <div className="sources-col-actions">
                              <button
                                className="sources-action-icon-btn sources-ideas-btn"
                                onClick={() => handleQuickCreateIdeas(source)}
                                title="Generate ideas from this source"
                              >
                                <Icon name="lightbulb" size="1rem" />
                              </button>
                              <button
                                className="sources-action-icon-btn sources-content-btn"
                                onClick={() => handleQuickCreateContent(source)}
                                title="Generate content from this source"
                              >
                                <Icon name="pencil" size="1rem" />
                              </button>
                              <button
                                className="sources-delete-icon-btn"
                                onClick={() => handleDeleteSource(source._id)}
                                title="Delete source"
                              >
                                <Icon name="trash-empty" size="1rem" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                        )}
                      </>
                    )}

                    {/* Add Mode - Show Add Source Form */}
                    {viewMode === 'add' && (
                      <div className="sources-add-form-inline">
                        <div className="sources-add-tabs">
                          <button
                            className={`sources-add-tab ${addSourceTab === 'url' ? 'active' : ''}`}
                            onClick={() => setAddSourceTab('url')}
                          >
                            <Icon name="link" size="1rem" /> URL Source
                          </button>
                          <button
                            className={`sources-add-tab ${addSourceTab === 'text' ? 'active' : ''}`}
                            onClick={() => setAddSourceTab('text')}
                          >
                            <Icon name="doc" size="1rem" /> Text Source
                          </button>
                          <button
                            className={`sources-add-tab ${addSourceTab === 'upload' ? 'active' : ''}`}
                            onClick={() => setAddSourceTab('upload')}
                          >
                            <Icon name="upload-cloud" size="1rem" /> Upload File
                          </button>
                          <button
                            className={`sources-add-tab ${addSourceTab === 'drive' ? 'active' : ''}`}
                            onClick={() => setAddSourceTab('drive')}
                          >
                            <Icon name="google" size="1rem" /> Google Drive
                          </button>
                        </div>

                        <div className="sources-form-container">
                          {addSourceTab === 'url' && (
                            <div className="sources-form">
                              <div className="sources-form-group">
                                <label>Source Name *</label>
                                <input
                                  type="text"
                                  placeholder="e.g., Company Blog Post"
                                  value={urlName}
                                  onChange={(e) => setUrlName(e.target.value)}
                                  autoComplete="off"
                                  data-1p-ignore="true"
                                  data-lpignore="true"
                                  data-bwignore="true"
                                  data-form-type="other"
                                />
                              </div>
                              <div className="sources-form-group">
                                <label>URL *</label>
                                <input
                                  type="url"
                                  placeholder="https://example.com/article"
                                  value={urlInput}
                                  onChange={(e) => setUrlInput(e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && handleAddUrl()}
                                  autoComplete="off"
                                  data-1p-ignore="true"
                                  data-lpignore="true"
                                  data-bwignore="true"
                                  data-form-type="other"
                                />
                              </div>
                              <div className="sources-form-actions">
                                <button
                                  className="sources-btn-cancel"
                                  onClick={() => setViewMode('view')}
                                  type="button"
                                >
                                  Cancel
                                </button>
                                <button
                                  className="sources-btn-add"
                                  onClick={handleAddUrl}
                                  disabled={addingUrl || !urlName.trim() || !urlInput.trim()}
                                >
                                  {addingUrl ? (
                                    <>
                                      Adding...
                                    </>
                                  ) : (
                                    <>
                                      Add URL Source
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}

                          {addSourceTab === 'text' && (
                            <div className="sources-form">
                              <div className="sources-form-group">
                                <label>Source Name *</label>
                                <input
                                  type="text"
                                  placeholder="e.g., Product Description"
                                  value={textName}
                                  onChange={(e) => setTextName(e.target.value)}
                                  autoComplete="off"
                                  data-1p-ignore="true"
                                  data-lpignore="true"
                                  data-bwignore="true"
                                  data-form-type="other"
                                />
                              </div>
                              <div className="sources-form-group">
                                <label>Text Content *</label>
                                <textarea
                                  placeholder="Paste or type your text content here..."
                                  value={textInput}
                                  onChange={(e) => setTextInput(e.target.value)}
                                  rows={10}
                                />
                              </div>
                              <div className="sources-form-actions">
                                <button
                                  className="sources-btn-cancel"
                                  onClick={() => setViewMode('view')}
                                  type="button"
                                >
                                  Cancel
                                </button>
                                <button
                                  className="sources-btn-add"
                                  onClick={handleAddText}
                                  disabled={addingText || !textName.trim() || !textInput.trim()}
                                >
                                  {addingText ? (
                                    <>
                                      Adding...
                                    </>
                                  ) : (
                                    <>
                                      Add Text Source
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}

                          {addSourceTab === 'upload' && (
                            <>
                              {!showTranscriptionPreview ? (
                                <div className="sources-form">
                                  {!uploadKind ? (
                                    <div
                                      className={`sources-upload-dropzone ${isDraggingUpload ? 'drag-over' : ''}`}
                                      onDragEnter={handleUploadDragEnter}
                                      onDragLeave={handleUploadDragLeave}
                                      onDragOver={handleUploadDragOver}
                                      onDrop={handleUploadDrop}
                                      onClick={() => document.getElementById('sources-upload-file-input').click()}
                                    >
                                      <input
                                        id="sources-upload-file-input"
                                        type="file"
                                        accept={UPLOAD_FILE_ACCEPT}
                                        onChange={(e) => {
                                          const file = e.target.files[0];
                                          if (file) handleUploadFileSelect(file);
                                          e.target.value = '';
                                        }}
                                        style={{ display: 'none' }}
                                      />
                                      <div className="sources-upload-dropzone-icon">
                                        <Icon name="upload-cloud" size="2.5rem" />
                                      </div>
                                      <p className="sources-upload-dropzone-text">
                                        <strong>Drag and drop</strong> a file here, or <strong>click to browse</strong>
                                      </p>
                                      <p className="sources-upload-dropzone-hint">
                                        Images, PDFs, audio, or video - we'll sort the type automatically
                                      </p>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="sources-form-group">
                                        <label>Source Name *</label>
                                        <input
                                          type="text"
                                          placeholder="e.g., Product Whitepaper"
                                          value={uploadName}
                                          onChange={(e) => setUploadName(e.target.value)}
                                          autoComplete="off"
                                          data-1p-ignore="true"
                                          data-lpignore="true"
                                          data-bwignore="true"
                                          data-form-type="other"
                                        />
                                      </div>
                                      <div className="sources-upload-file-preview">
                                        {uploadingFile ? (
                                          <div className="sources-upload-progress">
                                            <div className="loading-spinner"></div>
                                            <p>Uploading... {uploadProgress}%</p>
                                          </div>
                                        ) : (
                                          <>
                                            <div className="sources-upload-file-icon">
                                              <Icon
                                                name={uploadKind === 'image' ? 'picture' : uploadKind === 'pdf' ? 'doc-text' : 'play'}
                                                size="1.5rem"
                                              />
                                            </div>
                                            <div className="sources-upload-file-info">
                                              <div className="sources-upload-file-name">
                                                {uploadKind === 'audiovideo' ? audioVideoFile?.name : uploadFileInfo?.name}
                                              </div>
                                              <div className="sources-upload-file-meta">
                                                {uploadKind === 'audiovideo' && audioVideoFile
                                                  ? `${(audioVideoFile.size / (1024 * 1024)).toFixed(2)} MB`
                                                  : uploadFileInfo?.size}
                                              </div>
                                            </div>
                                            <button
                                              type="button"
                                              className="sources-upload-remove-btn"
                                              onClick={resetUploadTab}
                                              title="Remove file"
                                            >
                                              ×
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </>
                                  )}
                                  <div className="sources-form-actions">
                                    <button
                                      className="sources-btn-cancel"
                                      onClick={() => setViewMode('view')}
                                      type="button"
                                    >
                                      Cancel
                                    </button>
                                    {uploadKind === 'image' && (
                                      <button
                                        className="sources-btn-add"
                                        onClick={handleAddMedia}
                                        disabled={addingMedia || uploadingFile || !uploadName.trim() || !mediaFile}
                                      >
                                        {addingMedia ? 'Adding...' : 'Add Source'}
                                      </button>
                                    )}
                                    {uploadKind === 'pdf' && (
                                      <button
                                        className="sources-btn-add"
                                        onClick={handleAddPdf}
                                        disabled={addingPdf || uploadingFile || !uploadName.trim() || !pdfFile}
                                      >
                                        {addingPdf ? 'Adding...' : 'Add Source'}
                                      </button>
                                    )}
                                    {uploadKind === 'audiovideo' && (
                                      <button
                                        className="sources-btn-add"
                                        onClick={handleAddAudioVideo}
                                        disabled={addingAudioVideo || !uploadName.trim() || !audioVideoFile}
                                      >
                                        {addingAudioVideo ? 'Processing...' : 'Add & Transcribe'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="sources-transcription-preview">
                                  <h4>Transcription Preview</h4>
                                  <p className="sources-preview-instruction">
                                    {showTranscriptionComparison
                                      ? 'Select Which Transcription to Keep'
                                      : 'Review the transcription below. If it looks incorrect, use the "Transcription Issue?" button to try an alternative transcription method.'
                                    }
                                  </p>

                                  {showTranscriptionComparison ? (
                                    <div className="sources-inline-comparison">
                                      <div className="sources-comparison-grid">
                                        <div className="sources-comparison-column">
                                          <h6>Original Transcription</h6>
                                          <div className="sources-comparison-text">
                                            <pre>{transcriptionResult}</pre>
                                          </div>
                                          <button
                                            className="sources-select-transcription-btn"
                                            onClick={() => handleSaveTranscription(transcriptionResult)}
                                          >
                                            Keep This Version
                                          </button>
                                        </div>
                                        <div className="sources-comparison-column">
                                          <h6>Alternative Transcription</h6>
                                          <div className="sources-comparison-text">
                                            <pre>{alternativeTranscription}</pre>
                                          </div>
                                          <button
                                            className="sources-select-transcription-btn"
                                            onClick={() => handleSaveTranscription(alternativeTranscription)}
                                          >
                                            Keep This Version
                                          </button>
                                        </div>
                                      </div>
                                      <div className="sources-comparison-cancel">
                                        <button
                                          className="sources-btn-cancel"
                                          onClick={async () => {
                                            const confirmed = await modal.confirm(
                                              'Are you sure you want to cancel? The transcription will be discarded and you will need to upload the file again.',
                                              'Cancel Transcription'
                                            );
                                            if (confirmed) {
                                              setShowTranscriptionPreview(false);
                                              setShowTranscriptionComparison(false);
                                              setTranscriptionResult('');
                                              setAlternativeTranscription('');
                                            }
                                          }}
                                        >
                                          Cancel - Neither Looks Good
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="sources-transcription-text-preview">
                                        <pre>{transcriptionResult}</pre>
                                      </div>

                                      <div className="sources-preview-actions">
                                        <button
                                          className="sources-retranscribe-btn"
                                          onClick={async () => {
                                            const confirmed = await modal.confirm(
                                              'Only use this if the current transcription is incorrect. This will try an alternative transcription method.',
                                              'Re-transcribe Source?'
                                            );
                                            if (confirmed) {
                                              handleRetranscribeAudio();
                                            }
                                          }}
                                          disabled={retranscribing}
                                        >
                                          <Icon name="attention" size="1rem" />
                                          {retranscribing ? 'Processing...' : 'Transcription Issue?'}
                                        </button>
                                        <div className="sources-preview-save-actions">
                                          <button
                                            className="sources-btn-cancel"
                                            onClick={() => {
                                              setShowTranscriptionPreview(false);
                                              setTranscriptionResult('');
                                            }}
                                          >
                                            Cancel
                                          </button>
                                          <button
                                            className="sources-btn-add"
                                            onClick={() => handleSaveTranscription(transcriptionResult)}
                                            disabled={addingAudioVideo}
                                          >
                                            {addingAudioVideo ? 'Saving...' : 'Save Source'}
                                          </button>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </>
                          )}

                          {addSourceTab === 'drive' && (
                            <div className="sources-form">
                              <GoogleDrivePicker
                                selectedFile={driveFile}
                                onSelect={(file) => {
                                  setDriveFile(file);
                                  // Auto-set name from file
                                  setDriveName(file.name);
                                }}
                              />
                              <div className="sources-form-actions">
                                <button
                                  className="sources-btn-cancel"
                                  onClick={() => setViewMode('view')}
                                  type="button"
                                >
                                  Cancel
                                </button>
                                <button
                                  className="sources-btn-add"
                                  onClick={handleAddDrive}
                                  disabled={addingDrive || !driveFile}
                                >
                                  {addingDrive ? (
                                    <>
                                      Importing...
                                    </>
                                  ) : (
                                    <>
                                      Import from Google Drive
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Create Ideas Mode - Source Selection */}
                    {viewMode === 'create-ideas' && (
                      <div className="sources-generation-form">
                        <div className="sources-generation-header">
                          <h4>Generate Ideas from Sources</h4>
                          <p>Select sources to generate content ideas from</p>
                        </div>

                        <div className="sources-generation-content">
                          {/* Organization & Brand & Amount Selection */}
                          <div className="sources-generation-org-brand">
                            <div className="sources-form-group">
                              <label>Organization</label>
                              <CustomSelect
                                value={generationOrgId || ''}
                                onChange={(e) => {
                                  const selectedOrgId = e.target.value;
                                  setGenerationOrgId(selectedOrgId);

                                  // Auto-select first brand alphabetically
                                  if (selectedOrgId) {
                                    const brandsForOrg = brands
                                      .filter(b => b.organization_id === selectedOrgId)
                                      .sort((a, b) => {
                                        const nameA = (a.brand_name || a.name || '').toLowerCase();
                                        const nameB = (b.brand_name || b.name || '').toLowerCase();
                                        return nameA.localeCompare(nameB);
                                      });

                                    if (brandsForOrg.length > 0) {
                                      setGenerationBrandId(brandsForOrg[0]._id || brandsForOrg[0].id);
                                    } else {
                                      setGenerationBrandId(null);
                                    }
                                  } else {
                                    setGenerationBrandId(null);
                                  }
                                }}
                                className="sources-select"
                              >
                                <option value="">Select Organization</option>
                                {organizations
                                  .slice()
                                  .sort((a, b) => {
                                    const nameA = (a.organization_name || a.name || '').toLowerCase();
                                    const nameB = (b.organization_name || b.name || '').toLowerCase();
                                    return nameA.localeCompare(nameB);
                                  })
                                  .map(org => (
                                    <option key={org._id || org.id} value={org._id || org.id}>
                                      {org.organization_name || org.name}
                                    </option>
                                  ))}
                              </CustomSelect>
                            </div>

                            <div className="sources-form-group">
                              <label>Brand</label>
                              <CustomSelect
                                value={generationBrandId || ''}
                                onChange={(e) => setGenerationBrandId(e.target.value)}
                                className="sources-select"
                                disabled={!generationOrgId}
                              >
                                <option value="">Select Brand</option>
                                {brands
                                  .filter(b => b.organization_id === generationOrgId)
                                  .sort((a, b) => {
                                    const nameA = (a.brand_name || a.name || '').toLowerCase();
                                    const nameB = (b.brand_name || b.name || '').toLowerCase();
                                    return nameA.localeCompare(nameB);
                                  })
                                  .map(brand => (
                                    <option key={brand._id || brand.id} value={brand._id || brand.id}>
                                      {brand.brand_name || brand.name}
                                    </option>
                                  ))}
                              </CustomSelect>
                            </div>

                            <div className="sources-form-group">
                              <label>Amount</label>
                              <input
                                type="number"
                                min="1"
                                max="20"
                                value={generationAmount}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value);
                                  if (value >= 1 && value <= 20) {
                                    setGenerationAmount(value);
                                  }
                                }}
                                className="sources-select"
                                autoComplete="off"
                                data-1p-ignore="true"
                                data-lpignore="true"
                                data-bwignore="true"
                                data-form-type="other"
                              />
                            </div>
                          </div>

                          {/* 2-Column Layout: Available Sources (Left) and Selected Sources (Right) */}
                          {generationBrandId && (
                            <div className="sources-two-column-layout">
                              {/* Left Column - Available Sources */}
                              <div className="sources-column-left">
                                <div className="sources-generation-list-header">
                                  <h5>Available Sources</h5>
                                  <span className="sources-selected-count">
                                    {sources.filter(s => s.brand_id === generationBrandId).length} source{sources.filter(s => s.brand_id === generationBrandId).length !== 1 ? 's' : ''}
                                  </span>
                                </div>

                                <div className="sources-generation-list">
                                  {sources
                                    .filter(s => (s.brand_id === generationBrandId))
                                    .map(source => (
                                      <label key={source._id} className="sources-checkbox-item">
                                        <input
                                          type="checkbox"
                                          checked={selectedSourcesForGeneration.includes(source._id)}
                                          onChange={() => toggleSourceSelection(source)}
                                        />
                                        <div className="sources-checkbox-content">
                                          <div className="sources-checkbox-icon">
                                            <Icon name={getSourceIcon(source.source_type)} size="1rem" />
                                          </div>
                                          <div className="sources-checkbox-info">
                                            <strong>{source.name}</strong>
                                            <span className="sources-checkbox-meta">
                                              {getSourceTypeName(source)} • {formatDate(source.created_at)}
                                            </span>
                                          </div>
                                        </div>
                                      </label>
                                    ))}
                                </div>
                              </div>

                              {/* Right Column - Selected Sources */}
                              <div className="sources-column-right">
                                <div className="sources-generation-list-header">
                                  <h5>Selected Sources</h5>
                                  <span className="sources-selected-count">
                                    {selectedSourcesDetails.length} source{selectedSourcesDetails.length !== 1 ? 's' : ''}
                                  </span>
                                </div>

                                {selectedSourcesDetails.length > 0 ? (
                                  <div className="sources-generation-list sources-selected-list">
                                    {selectedSourcesDetails.map(source => (
                                      <div key={source._id} className="sources-selected-item">
                                        <div className="sources-checkbox-content">
                                          <div className="sources-checkbox-icon">
                                            <Icon name={getSourceIcon(source.source_type)} size="1rem" />
                                          </div>
                                          <div className="sources-checkbox-info">
                                            <strong>{source.name}</strong>
                                            <span className="sources-checkbox-meta">
                                              {getSourceTypeName(source)} • {getOrgName(source.organization_id)} / {getBrandName(source.brand_id)}
                                            </span>
                                          </div>
                                        </div>
                                        <button
                                          className="sources-remove-selected-btn"
                                          onClick={() => removeSelectedSource(source._id)}
                                          title="Remove source"
                                        >
                                          <Icon name="cancel" size="1rem" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="sources-empty-selected">
                                    <Icon name="doc" size="2rem" />
                                    <p>No sources selected</p>
                                    <span>Select sources from the list on the left</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="sources-generation-actions">
                            <button
                              className="sources-cancel-btn"
                              onClick={() => {
                                setViewMode('view');
                                setSelectedSourcesForGeneration([]);
                                setSelectedSourcesDetails([]);
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              className="sources-generate-btn"
                              onClick={handleGenerateIdeas}
                              disabled={selectedSourcesForGeneration.length === 0 || generatingIdeas}
                            >
                              {generatingIdeas ? 'Generating...' : 'Generate Ideas'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Create Content Mode - Source Selection */}
                    {viewMode === 'create-content' && (
                      <div className="sources-generation-form">
                        <div className="sources-generation-header">
                          <h4>Generate Content from Sources</h4>
                          <p>Select sources to generate content from</p>
                        </div>

                        <div className="sources-generation-content">
                          {/* Organization & Brand Selection */}
                          <div className="sources-generation-org-brand">
                            <div className="sources-form-group">
                              <label>Organization</label>
                              <CustomSelect
                                value={generationOrgId || ''}
                                onChange={(e) => {
                                  const selectedOrgId = e.target.value;
                                  setGenerationOrgId(selectedOrgId);

                                  // Auto-select first brand alphabetically
                                  if (selectedOrgId) {
                                    const brandsForOrg = brands
                                      .filter(b => b.organization_id === selectedOrgId)
                                      .sort((a, b) => {
                                        const nameA = (a.brand_name || a.name || '').toLowerCase();
                                        const nameB = (b.brand_name || b.name || '').toLowerCase();
                                        return nameA.localeCompare(nameB);
                                      });

                                    if (brandsForOrg.length > 0) {
                                      setGenerationBrandId(brandsForOrg[0]._id || brandsForOrg[0].id);
                                    } else {
                                      setGenerationBrandId(null);
                                    }
                                  } else {
                                    setGenerationBrandId(null);
                                  }
                                }}
                                className="sources-select"
                              >
                                <option value="">Select Organization</option>
                                {organizations
                                  .slice()
                                  .sort((a, b) => {
                                    const nameA = (a.organization_name || a.name || '').toLowerCase();
                                    const nameB = (b.organization_name || b.name || '').toLowerCase();
                                    return nameA.localeCompare(nameB);
                                  })
                                  .map(org => (
                                    <option key={org._id || org.id} value={org._id || org.id}>
                                      {org.organization_name || org.name}
                                    </option>
                                  ))}
                              </CustomSelect>
                            </div>

                            <div className="sources-form-group">
                              <label>Brand</label>
                              <CustomSelect
                                value={generationBrandId || ''}
                                onChange={(e) => setGenerationBrandId(e.target.value)}
                                className="sources-select"
                                disabled={!generationOrgId}
                              >
                                <option value="">Select Brand</option>
                                {brands
                                  .filter(b => b.organization_id === generationOrgId)
                                  .sort((a, b) => {
                                    const nameA = (a.brand_name || a.name || '').toLowerCase();
                                    const nameB = (b.brand_name || b.name || '').toLowerCase();
                                    return nameA.localeCompare(nameB);
                                  })
                                  .map(brand => (
                                    <option key={brand._id || brand.id} value={brand._id || brand.id}>
                                      {brand.brand_name || brand.name}
                                    </option>
                                  ))}
                              </CustomSelect>
                            </div>
                          </div>

                          {/* Content Type and Quantity Selection */}
                          <div className="sources-generation-org-brand">
                            <div className="sources-form-group">
                              <label>Content Type</label>
                              <CustomSelect
                                  value={contentType}
                                  onChange={(e) => setContentType(e.target.value)}
                                  className="sources-select"
                                >
                                  <option value="social">Social Media</option>
                                  <option value="blog">Blog Post</option>
                                  <option value="video">Video Script</option>
                                  <option value="newsletter">Newsletter</option>
                                  <option value="podcast">Podcast</option>
                                </CustomSelect>
                            </div>

                            <div className="sources-form-group">
                              <label>Length</label>
                              <CustomSelect
                                  value={contentLength}
                                  onChange={(e) => setContentLength(e.target.value)}
                                  className="sources-select"
                                >
                                  <option value="short">Short</option>
                                  <option value="medium">Medium</option>
                                  <option value="long">Long</option>
                                </CustomSelect>
                            </div>

                            <div className="sources-form-group">
                              <label>Quantity</label>
                              <input
                                type="number"
                                min="1"
                                max="5"
                                value={contentQuantity}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value);
                                  if (value >= 1 && value <= 5) {
                                    setContentQuantity(value);
                                  }
                                }}
                                className="sources-select"
                                autoComplete="off"
                                data-1p-ignore="true"
                                data-lpignore="true"
                                data-bwignore="true"
                                data-form-type="other"
                              />
                            </div>
                          </div>

                          {/* 2-Column Layout: Available Items (Left) and Selected Items (Right) */}
                          {generationBrandId && (
                            <div className="sources-two-column-layout">
                              {/* Left Column - Available Sources or Ideas */}
                              <div className="sources-column-left">
                                {/* Tab Header */}
                                <div className="sources-generation-list-header sources-tab-header">
                                  <button
                                    className={`sources-tab-header-btn ${sourceOrIdeaTab === 'sources' ? 'active' : ''}`}
                                    onClick={() => setSourceOrIdeaTab('sources')}
                                  >
                                    <Icon name="doc" size="0.9rem" /> Available Sources
                                    <span className="sources-tab-count">{sources.filter(s => s.brand_id === generationBrandId).length}</span>
                                  </button>
                                  <button
                                    className={`sources-tab-header-btn ${sourceOrIdeaTab === 'ideas' ? 'active' : ''}`}
                                    onClick={() => setSourceOrIdeaTab('ideas')}
                                  >
                                    <Icon name="lightbulb" size="0.9rem" /> Available Ideas
                                    <span className="sources-tab-count">{availableIdeas.length}</span>
                                  </button>
                                </div>

                                {sourceOrIdeaTab === 'sources' ? (
                                  <>
                                    <div className="sources-generation-list">
                                      {sources
                                        .filter(s => (s.brand_id === generationBrandId))
                                        .map(source => (
                                          <label key={source._id} className="sources-checkbox-item">
                                            <input
                                              type="checkbox"
                                              checked={selectedSourcesForGeneration.includes(source._id)}
                                              onChange={() => toggleSourceSelection(source)}
                                            />
                                            <div className="sources-checkbox-content">
                                              <div className="sources-checkbox-icon">
                                                <Icon name={getSourceIcon(source.source_type)} size="1rem" />
                                              </div>
                                              <div className="sources-checkbox-info">
                                                <strong>{source.name}</strong>
                                                <span className="sources-checkbox-meta">
                                                  {getSourceTypeName(source)} • {formatDate(source.created_at)}
                                                </span>
                                              </div>
                                            </div>
                                          </label>
                                        ))}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="sources-generation-list">
                                      {availableIdeas.length > 0 ? (
                                        availableIdeas.map(idea => (
                                          <label key={idea.id} className="sources-checkbox-item">
                                            <input
                                              type="checkbox"
                                              checked={selectedIdeasForGeneration.includes(idea.id)}
                                              onChange={() => toggleIdeaSelection(idea)}
                                            />
                                            <div className="sources-checkbox-content">
                                              <div className="sources-checkbox-icon">
                                                <Icon name="lightbulb" size="1rem" />
                                              </div>
                                              <div className="sources-checkbox-info">
                                                <strong>{idea.title}</strong>
                                                <span className="sources-checkbox-meta">
                                                  {idea.content_type} • {idea.synopsis?.substring(0, 60)}{idea.synopsis?.length > 60 ? '...' : ''}
                                                </span>
                                              </div>
                                            </div>
                                          </label>
                                        ))
                                      ) : (
                                        <div className="sources-empty-state">
                                          <Icon name="lightbulb" size="2rem" />
                                          <p>No ideas available</p>
                                          <span>Create ideas first to use them as sources</span>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>

                              {/* Right Column - Selected Sources and Ideas */}
                              <div className="sources-column-right">
                                <div className="sources-generation-list-header">
                                  <h5>Selected Items</h5>
                                  <span className="sources-selected-count">
                                    {selectedSourcesDetails.length + selectedIdeasDetails.length} item{(selectedSourcesDetails.length + selectedIdeasDetails.length) !== 1 ? 's' : ''}
                                  </span>
                                </div>

                                {(selectedSourcesDetails.length > 0 || selectedIdeasDetails.length > 0) ? (
                                  <div className="sources-generation-list sources-selected-list">
                                    {selectedSourcesDetails.map(source => (
                                      <div key={`source-${source._id}`} className="sources-selected-item">
                                        <div className="sources-checkbox-content">
                                          <div className="sources-checkbox-icon">
                                            <Icon name={getSourceIcon(source.source_type)} size="1rem" />
                                          </div>
                                          <div className="sources-checkbox-info">
                                            <strong>{source.name}</strong>
                                            <span className="sources-checkbox-meta">
                                              {getSourceTypeName(source)} • Source
                                            </span>
                                          </div>
                                        </div>
                                        <button
                                          className="sources-remove-selected-btn"
                                          onClick={() => removeSelectedSource(source._id)}
                                          title="Remove source"
                                        >
                                          <Icon name="cancel" size="1rem" />
                                        </button>
                                      </div>
                                    ))}
                                    {selectedIdeasDetails.map(idea => (
                                      <div key={`idea-${idea.id}`} className="sources-selected-item">
                                        <div className="sources-checkbox-content">
                                          <div className="sources-checkbox-icon">
                                            <Icon name="lightbulb" size="1rem" />
                                          </div>
                                          <div className="sources-checkbox-info">
                                            <strong>{idea.title}</strong>
                                            <span className="sources-checkbox-meta">
                                              {idea.content_type} • Idea
                                            </span>
                                          </div>
                                        </div>
                                        <button
                                          className="sources-remove-selected-btn"
                                          onClick={() => removeSelectedIdea(idea.id)}
                                          title="Remove idea"
                                        >
                                          <Icon name="cancel" size="1rem" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="sources-empty-selected">
                                    <Icon name="doc" size="2rem" />
                                    <p>No items selected</p>
                                    <span>Select sources or ideas from the list on the left</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="sources-generation-actions">
                            <button
                              className="sources-cancel-btn"
                              onClick={() => {
                                setViewMode('view');
                                setSelectedSourcesForGeneration([]);
                                setSelectedSourcesDetails([]);
                                setSelectedIdeasForGeneration([]);
                                setSelectedIdeasDetails([]);
                                setContentType('social');
                                setContentQuantity(1);
                                setSourceOrIdeaTab('sources');
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              className="sources-generate-btn"
                              onClick={handleGenerateContent}
                              disabled={(selectedSourcesForGeneration.length === 0 && selectedIdeasForGeneration.length === 0) || generatingContent}
                            >
                              {generatingContent ? 'Generating...' : `Generate ${contentQuantity > 1 ? contentQuantity + ' ' : ''}Content${contentQuantity > 1 ? ' Pieces' : ''}`}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* View Source Modal */}
      {viewSourceModal.open && viewSourceModal.source && (
        <div className="sources-modal-overlay" onClick={closeSourceViewer}>
          <div className="sources-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sources-modal-header">
              <div>
                <h3>{viewSourceModal.source.name}</h3>
                <div className="sources-view-modal-meta">
                  <span className="sources-type-badge-inline">
                    <Icon name={getSourceIcon(viewSourceModal.source.source_type)} size="0.875rem" />
                    {getSourceTypeName(viewSourceModal.source)}
                  </span>
                  <span className="sources-meta-divider">•</span>
                  <span className="sources-meta-date">
                    Created {formatDate(viewSourceModal.source.created_at)}
                  </span>
                </div>
              </div>
              <div className="sources-modal-header-actions">
                {viewSourceModal.source.content && (
                  <button
                    className={`sources-copy-btn ${sourceCopied ? 'copied' : ''}`}
                    onClick={copySourceContent}
                  >
                    <Icon name={sourceCopied ? 'ok' : 'docs'} size="1rem" />
                    {sourceCopied ? 'Copied!' : 'Copy'}
                  </button>
                )}
                <button className="modal-close-btn" onClick={closeSourceViewer} aria-label="Close" />
              </div>
            </div>
            <div className="sources-view-modal-body">
              {viewSourceModal.source.source_type === 'url' ? (
                <div className="sources-url-content">
                  <div className="sources-url-display">
                    <Icon name="link" size="1.25rem" />
                    <a
                      href={viewSourceModal.source.original_url || viewSourceModal.source.content}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sources-url-value"
                    >
                      {viewSourceModal.source.original_url || viewSourceModal.source.content}
                    </a>
                  </div>
                  {viewSourceModal.source.content && viewSourceModal.source.content !== viewSourceModal.source.original_url && (
                    <div className="sources-url-scraped-content">
                      <h4>Scraped Content:</h4>
                      <pre className="sources-content-text">{viewSourceModal.source.content}</pre>
                    </div>
                  )}
                </div>
              ) : viewSourceModal.source.source_type === 'meeting' || viewSourceModal.source.source_type === 'transcription' || viewSourceModal.source.source_type === 'squadcast_transcript' ? (
                <div className="sources-transcript-content">
                  {(() => {
                    const content = viewSourceModal.source.content;
                    if (!content) {
                      return <p className="sources-no-content">No transcript available.</p>;
                    }
                    // Handle case where content is an object (transcript data structure)
                    if (typeof content === 'object' && content !== null) {
                      const textContent = content.formatted || content.simple || JSON.stringify(content, null, 2);
                      return <pre className="sources-content-text sources-transcript-text">{textContent}</pre>;
                    }
                    // Handle case where content is a string
                    return <pre className="sources-content-text sources-transcript-text">{content}</pre>;
                  })()}
                </div>
              ) : viewSourceModal.source.source_type === 'brand_profile' ? (
                <div className="sources-brand-profile-content">
                  {(() => {
                    const content = viewSourceModal.source.content;
                    if (!content) {
                      return <p className="sources-no-content">No brand profile data available.</p>;
                    }

                    let profileData;
                    try {
                      profileData = typeof content === 'string' ? JSON.parse(content) : content;
                    } catch (e) {
                      return <pre className="sources-content-text">{content}</pre>;
                    }

                    const renderSection = (title, content, isList = false) => {
                      if (!content || (Array.isArray(content) && content.length === 0) || (typeof content === 'string' && !content.trim())) {
                        return null;
                      }

                      return (
                        <div className="brand-profile-section">
                          <h4 className="brand-profile-section-title">{title}</h4>
                          {isList ? (
                            <ul className="brand-profile-list">
                              {content.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="brand-profile-text">{content}</p>
                          )}
                        </div>
                      );
                    };

                    return (
                      <div className="brand-profile-container">
                        {renderSection('Target Audience', profileData.audience)}
                        {renderSection('Mission & Core Values', profileData.mission_core_values)}
                        {renderSection('Value Propositions', profileData.value_propositions, true)}
                        {renderSection('Key Differentiators', profileData.differentiators, true)}
                        {renderSection('Products & Services', profileData.products_services)}
                        {renderSection('Locations', profileData.locations, true)}
                        {renderSection('Ideal Clients', profileData.ideal_clients)}
                        {renderSection('Industry Details', profileData.industry_details)}
                        {renderSection('Competitors', profileData.competitors, true)}
                        {renderSection('Offers', profileData.offers, true)}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="sources-text-content">
                  {viewSourceModal.source.content ? (
                    <pre className="sources-content-text">{viewSourceModal.source.content}</pre>
                  ) : (
                    <p className="sources-no-content">No content available.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SourcesPanel;
